import { createEffect, on, Show } from "solid-js";
import { ElementNode } from "@solidtv/solid";
import { Row, useFocusStack } from "@solidtv/solid/primitives";
import { Button } from ".";
import { entityState, setEntityState, setPlayerState } from "../overlayState";
import { getManifestUrl } from "../api/jw";
import { setGlobalBackground } from "../state";
import theme from "theme";

export default function EntityOverlay() {
  const { storeFocus, restoreFocus } = useFocusStack();
  let actionRef: ElementNode;

  createEffect(
    on(
      entityState,
      (state) => {
        if (state) {
          storeFocus();
          setGlobalBackground(state.backdrop);
          queueMicrotask(() => actionRef?.setFocus());
        }
      },
      { defer: true }
    )
  );

  function close() {
    setEntityState(null);
    restoreFocus();
    return true;
  }

  function onPlay() {
    const state = entityState();
    if (!state?.sources) return;

    const manifestUrl = getManifestUrl(state.sources);
    if (!manifestUrl) return;

    setPlayerState({
      manifestUrl,
      title: state.title,
      isLive: state.isLive || false,
      tracks: state.tracks
    });
  }

  const entity = () => entityState();
  const hasPlayableSource = () => {
    const e = entity();
    if (!e?.sources) return false;
    return !!getManifestUrl(e.sources);
  };

  const durationText = () => {
    const e = entity();
    if (!e?.duration || e.duration <= 0) return "";
    const mins = Math.floor(e.duration / 60);
    const hrs = Math.floor(mins / 60);
    const remainMins = mins % 60;
    if (hrs > 0) return `${hrs}h ${remainMins < 10 ? "0" : ""}${remainMins}min`;
    return `${mins} min`;
  };

  return (
    <Show when={entity()}>
      <view
        x={0}
        y={0}
        width={theme.layout.screenW}
        height={1080}
        zIndex={200}
        color={0x000000cc}
        onBack={close}
        onBackspace={close}
        onEscape={close}
      >
        {/* Poster */}
        <view
          x={120}
          y={120}
          width={400}
          height={600}
          src={entity()!.poster}
          borderRadius={12}
          onFail={(node) => (node.src = "./assets/fallback.png")}
        />

        {/* Info */}
        <view x={580} y={120} width={1200}>
          <text fontSize={48} fontWeight={"bold"} width={1100} maxLines={2} color={theme.textPrimary}>
            {entity()!.title}
          </text>

          <Show when={durationText()}>
            <text y={110} fontSize={22} color={theme.textSecondary}>
              {durationText()}
            </text>
          </Show>

          <text y={150} fontSize={24} width={1100} maxLines={6} lineHeight={36} color={0xccccccff}>
            {entity()!.description}
          </text>

          <Show when={entity()!.tags}>
            <text y={420} fontSize={18} color={theme.textSecondary}>
              {entity()!.tags}
            </text>
          </Show>
        </view>

        {/* Action buttons */}
        <Row ref={actionRef!} x={580} y={750} height={90} width={640} gap={40}>
          <Show when={hasPlayableSource()}>
            <Button width={300} autofocus onEnter={onPlay}>
              {entity()!.isLive ? "Watch Live" : "Play"}
            </Button>
          </Show>
        </Row>
      </view>
    </Show>
  );
}
